// LIBRERIE/IMPORTS
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import he from "he";

// HOOKS
import { useGetAgencyDetails } from '../../hooks/useGetAgencyDetails';
import { useGetAgentsByAgency } from "../../hooks/useGetAgents";

// COMPONENTS
import WidgetContacts from '../Widgets/WidgetContacts/WidgetContacts';
import { WidgetMail } from '../Widgets/WidgetMail/WidgetMail';
import SubHeaderDefault from '../Header/subHeader/SubHeaderDefault';
import ServerError from '../Error/ServerError';
import LoadingIndicator from '../Loaders/LoadingIndicator';
import AgentsSection from '../AboutUs/AgentsSection';
import MapLoader from '../Wrappers/GoogleMapsWrapper';

// CONFIGURAZIONI
import { appConfig } from "../../config/appConfig";

const showMap = appConfig.ui.contactUsPage.showMap;

interface MarkerInfo {
  lat: number;
  lng: number;
  name: string;
  adr?: string;
}
  
interface Props {
    contactForm: boolean;
    agencyId: number;
    showAgents: boolean;
}

const Contacts: React.FC<Props> = ({
    contactForm,
    agencyId,
    showAgents
}) => {
    const { t } = useTranslation();
    const { agency, loading: agencyLoading, error: agencyError } = useGetAgencyDetails(agencyId);

    const [markerInfo, setMarkerInfo] = useState<MarkerInfo[] | null>(null);
    const { agents, loading: agentsLoading, error: agentsError } = useGetAgentsByAgency(agencyId);

    useEffect(() => {
        if (agency) {
            setMarkerInfo(appConfig.map.markerData as MarkerInfo[]);
        } else {
            setMarkerInfo(null);
        }
    }, [agency, t]);

    // --- Rendering Logic ---
    if (agencyLoading) return <LoadingIndicator />;
    if (agencyError) return <ServerError message={`${String(agencyError) || t('contactPage.errors.agencyLoadFailed')}`} />;

    return (
        <>
            <Helmet>
                <title>{t('contactPage.pageTitle')} | { he.decode(`${agency?.agencyName}`) || 'Agenzia Immobiliare'}</title>
            </Helmet>

            <SubHeaderDefault title={t('contactPage.pageTitle')} isSeo={true}/>

            <main className="space-ptb bg-custom">
                <div className="container-fluid">
                    <div className="row justify-content-around">
                        <div className="col-12">
                            {showMap && <MapLoader markerInfo={markerInfo} />}
                        </div>
                        <div className="col-12 col-lg-8 mb-5 mb-lg-0">
                            <h2>{t('widgets.mail.title')}</h2>
                            <p dangerouslySetInnerHTML={{ __html: t('contactPage.para1') as string }}></p>
                            {contactForm &&
                                <WidgetMail />
                            }
                        </div>
                        <div className="col-12 col-lg-4 col-xl-3">
                            <AgentsSection
                                showAgents={showAgents}
                                agents={agents}
                                agentsLoading={agentsLoading}
                                agentsError={agentsError}
                            />
                            <WidgetContacts
                                agencyDataAddress={agency?.address_details?.address ?? ''}
                                agencyDataCity={agency?.address_details?.city_name ?? ''}
                                agencyDataComuneCapoluogo={agency?.address_details?.city_name === agency?.address_details?.province_name}
                                agencyDataProvinciaSigla={agency?.address_details?.sigla ?? ''}
                                agencyDataEmail={appConfig.agency.agencyEmail}
                                agencyDataEmailShow={appConfig.ui.agencyWidget.showEmail}
                                agencyDataPhone1={agency?.phone1 ?? ''}
                                agencyDataPhone2={agency?.phone2 ?? ''}
                                agencyDataInternationalPrefix={appConfig.agency.intlPrefix ?? '+39'}
                                agencyDataMobile={agency?.mobile ?? ''}
                                agencyDataMobile2={agency?.mobile2 ?? ''}
                                agencySocialShow={appConfig.ui.agencyWidget.showSocialLinks}
                                agencyDataWhatsapp={appConfig.socialLinks.whatsapp}
                                agencyDataFacebook={appConfig.socialLinks.facebook}
                                agencyDataInstagram={appConfig.socialLinks.instagram}
                                agencyDataTwitter={appConfig.socialLinks.twitter}
                                agencyDataLinkedin={appConfig.socialLinks.linkedin}
                                agencyDataPinterest={appConfig.socialLinks.pinterest}
                                agencyDataYoutube={appConfig.socialLinks.youtube}
                                agencyDataTikTok={appConfig.socialLinks.tiktok}
                                agencyDataThreads={appConfig.socialLinks.threads}
                            />
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
};

export default Contacts;